(function() {
  'use strict';

  if (typeof window.FPJQuiz !== 'undefined') {
    return;
  }

  class FPJQuiz {
    constructor(sectionId, dataUrl) {
      this.sectionId = sectionId;
      this.dataUrl = dataUrl;
      this.data = null;
      this.currentMode = null;
      this.questions = [];
      this.currentIndex = 0;
      this.score = 0;
      this.totalQuestions = 10;
      this.answered = false;

      // DOM elements
      this.container = document.getElementById('fpj-quiz-section');
      this.modeSelect = document.getElementById('fpj-mode-select');
      this.form = document.getElementById('fpj-quiz-form');
      this.results = document.getElementById('fpj-quiz-results');

      // Guard against missing DOM elements
      if (!this.container || !this.modeSelect || !this.form || !this.results) {
        console.error('FPJ Quiz: Required DOM elements not found');
        return;
      }

      // Image base URL from Shopify Files (set via data attribute)
      this.imageBase = this.container.dataset?.imageBase || '';

      this.init();
    }

    async init() {
      await this.loadData();
      this.attachEventListeners();
    }

    async loadData() {
      this.showLoading(true);
      try {
        // Add timeout to prevent hanging on slow connections
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 10000);

        const response = await fetch(this.dataUrl, { signal: controller.signal });
        clearTimeout(timeout);

        if (!response.ok) throw new Error('Failed to load quiz data');
        this.data = await response.json();
      } catch (error) {
        console.error('FPJ Quiz: Failed to load data', error);
        const message = error.name === 'AbortError'
          ? 'Quiz took too long to load. Please check your connection and refresh.'
          : 'Unable to load quiz. Please refresh the page.';
        this.showError(message);
      } finally {
        this.showLoading(false);
      }
    }

    showLoading(show) {
      if (!this.modeSelect) return;
      let loader = this.modeSelect.querySelector('.fpj-loading');

      if (show) {
        if (!loader) {
          loader = document.createElement('div');
          loader.className = 'fpj-loading';
          loader.textContent = 'Loading quiz...';
          this.modeSelect.insertBefore(loader, this.modeSelect.firstChild);
        }
        loader.style.display = 'block';
        // Hide mode buttons while loading
        this.modeSelect.querySelectorAll('.fpj-mode-btn').forEach(btn => {
          btn.style.opacity = '0.5';
          btn.style.pointerEvents = 'none';
        });
      } else {
        if (loader) loader.style.display = 'none';
        // Restore mode buttons
        this.modeSelect.querySelectorAll('.fpj-mode-btn').forEach(btn => {
          btn.style.opacity = '';
          btn.style.pointerEvents = '';
        });
      }
    }

    attachEventListeners() {
      // Mode buttons
      document.querySelectorAll('.fpj-mode-btn').forEach(btn => {
        btn.addEventListener('click', () => {
          this.startMode(btn.dataset.mode);
        });
      });

      // Back to modes
      document.querySelector('.fpj-back-to-modes')?.addEventListener('click', () => {
        this.showModeSelect();
      });

      // Next button
      document.getElementById('fpj-next-btn')?.addEventListener('click', () => {
        if (this.answered) {
          this.nextQuestion();
        }
      });

      // Results buttons
      document.getElementById('fpj-try-again-btn')?.addEventListener('click', () => {
        this.startMode(this.currentMode);
      });

      document.getElementById('fpj-new-mode-btn')?.addEventListener('click', () => {
        this.showModeSelect();
      });
    }

    startMode(mode) {
      this.currentMode = mode;
      this.currentIndex = 0;
      this.score = 0;
      this.answered = false;
      this.questions = this.generateQuestions(mode);

      this.modeSelect.style.display = 'none';
      this.results.style.display = 'none';
      this.form.style.display = 'block';

      this.updateScore();
      this.showQuestion();
    }

    generateQuestions(mode) {
      const models = this.shuffle([...this.data.models]);
      const questions = [];

      for (let i = 0; i < Math.min(this.totalQuestions, models.length); i++) {
        const model = models[i];
        questions.push(this.createQuestion(mode, model, models));
      }

      return questions;
    }

    createQuestion(mode, correctModel, allModels) {
      const otherModels = allModels.filter(m => m.name !== correctModel.name);
      const wrongOptions = this.shuffle(otherModels).slice(0, 3);

      switch (mode) {
        case 'name_that_journe':
          return {
            image: correctModel.image,
            text: 'What F.P. Journe model is this?',
            options: this.shuffle([
              { text: correctModel.name, correct: true },
              ...wrongOptions.map(m => ({ text: m.name, correct: false }))
            ]),
            explanation: `This is the ${correctModel.name} (${correctModel.reference}). ${correctModel.key_feature}`
          };

        case 'calibre_match':
          const wrongCalibres = this.shuffle(
            this.data.calibres.filter(c => c.id !== correctModel.calibre)
          ).slice(0, 3);
          return {
            image: correctModel.image,
            text: `What calibre powers the ${correctModel.name}?`,
            options: this.shuffle([
              { text: `Calibre ${correctModel.calibre}`, correct: true },
              ...wrongCalibres.map(c => ({ text: `Calibre ${c.id}`, correct: false }))
            ]),
            explanation: `The ${correctModel.name} uses Calibre ${correctModel.calibre}, a ${this.getCalibreType(correctModel.calibre)} movement with ${this.getCalibrePower(correctModel.calibre)} power reserve.`
          };

        case 'spec_challenge':
          const specs = [
            `${correctModel.case_size} case`,
            `${correctModel.power_reserve} power reserve`,
            correctModel.material.split(',')[0] + ' case',
            correctModel.complication
          ];
          const specHint = this.shuffle(specs).slice(0, 2).join(' • ');
          return {
            image: null,
            text: `Which model matches these specs?\n${specHint}`,
            options: this.shuffle([
              { text: correctModel.name, correct: true },
              ...wrongOptions.map(m => ({ text: m.name, correct: false }))
            ]),
            explanation: `The ${correctModel.name} features a ${correctModel.case_size} case with ${correctModel.power_reserve} power reserve. ${correctModel.key_feature}`
          };

        case 'collection_sort':
          const wrongCollections = this.data.collections.filter(c => c.id !== correctModel.collection);
          return {
            image: correctModel.image,
            text: 'Which collection does this watch belong to?',
            options: this.shuffle([
              { text: correctModel.collection, correct: true },
              ...wrongCollections.map(c => ({ text: c.id, correct: false }))
            ]),
            explanation: `This is the ${correctModel.name}, part of the ${correctModel.collection} collection, which focuses on ${this.getCollectionFocus(correctModel.collection)}.`
          };

        default:
          return this.createQuestion('name_that_journe', correctModel, allModels);
      }
    }

    getCalibreType(calibreId) {
      const calibre = this.data.calibres.find(c => c.id === calibreId);
      return calibre ? calibre.type.toLowerCase() : 'mechanical';
    }

    getCalibrePower(calibreId) {
      const calibre = this.data.calibres.find(c => c.id === calibreId);
      return calibre ? calibre.power_reserve : 'variable';
    }

    getCollectionFocus(collectionId) {
      const collection = this.data.collections.find(c => c.id === collectionId);
      return collection ? collection.focus.toLowerCase() : 'fine watchmaking';
    }

    showQuestion() {
      const q = this.questions[this.currentIndex];
      this.answered = false;

      // Update progress
      document.querySelector('.fpj-quiz-progress').textContent =
        `Question ${this.currentIndex + 1} of ${this.questions.length}`;

      // Image
      const imageWrap = document.querySelector('.fpj-question-image-wrap');
      const imageEl = document.getElementById('fpj-question-image');
      if (q.image) {
        // Build full image URL - images are in Shopify Files after upload
        // For now, use the downloaded filename pattern
        imageEl.src = this.getImageUrl(q.image);
        imageEl.alt = 'F.P. Journe watch';
        imageWrap.style.display = 'block';
      } else {
        imageWrap.style.display = 'none';
      }

      // Question text
      document.getElementById('fpj-question-text').textContent = q.text;

      // Options
      const optionsContainer = document.getElementById('fpj-options');
      optionsContainer.innerHTML = q.options.map((opt, i) => `
        <label class="fpj-option" data-index="${i}" data-correct="${opt.correct}">
          <input type="radio" name="fpj-answer" value="${i}" />
          <span>${this.escapeHtml(opt.text)}</span>
        </label>
      `).join('');

      // Add click handlers
      optionsContainer.querySelectorAll('.fpj-option').forEach(opt => {
        opt.addEventListener('click', () => this.selectAnswer(opt));
      });

      // Hide feedback
      document.getElementById('fpj-feedback').style.display = 'none';

      // Disable next button
      document.getElementById('fpj-next-btn').disabled = true;
      document.getElementById('fpj-next-btn').textContent =
        this.currentIndex === this.questions.length - 1 ? 'See Results' : 'Next Question';
    }

    getImageUrl(filename) {
      // Images are stored in Shopify Files (Admin > Content > Files)
      // The base URL is passed from Liquid via data-image-base attribute
      if (this.imageBase && filename) {
        return this.imageBase + filename;
      }
      // Fallback: return filename for local testing
      return filename;
    }

    selectAnswer(optionEl) {
      if (this.answered) return;
      this.answered = true;

      const isCorrect = optionEl.dataset.correct === 'true';
      const q = this.questions[this.currentIndex];

      // Mark all options
      document.querySelectorAll('.fpj-option').forEach(opt => {
        opt.classList.add('disabled');
        opt.querySelector('input').disabled = true;

        if (opt.dataset.correct === 'true') {
          opt.classList.add('correct');
        } else if (opt === optionEl && !isCorrect) {
          opt.classList.add('incorrect');
        }
      });

      optionEl.classList.add('selected');

      // Update score
      if (isCorrect) {
        this.score++;
        this.updateScore();
      }

      // Show feedback
      const feedback = document.getElementById('fpj-feedback');
      feedback.style.display = 'block';
      feedback.className = 'fpj-feedback ' + (isCorrect ? 'correct' : 'incorrect');

      document.getElementById('fpj-feedback-text').textContent =
        isCorrect ? 'Correct!' : 'Not quite!';
      document.getElementById('fpj-feedback-detail').textContent = q.explanation;

      // Enable next button
      document.getElementById('fpj-next-btn').disabled = false;
    }

    nextQuestion() {
      if (this.currentIndex < this.questions.length - 1) {
        this.currentIndex++;
        this.showQuestion();
      } else {
        this.showResults();
      }
    }

    updateScore() {
      document.getElementById('fpj-current-score').textContent = this.score;
    }

    showResults() {
      this.form.style.display = 'none';
      this.results.style.display = 'block';

      const percentage = Math.round((this.score / this.questions.length) * 100);
      const tier = this.getMedalTier(percentage);

      document.getElementById('fpj-medal-icon').textContent = tier.emoji;
      document.getElementById('fpj-results-title').textContent = tier.title;
      document.getElementById('fpj-results-score').textContent =
        `${this.score} / ${this.questions.length} (${percentage}%)`;
      document.getElementById('fpj-results-message').textContent = tier.message;

      // Share links
      const shareText = encodeURIComponent(
        `I scored ${this.score}/${this.questions.length} on the F.P. Journe Quiz! ${tier.emoji} Can you beat my score?`
      );
      const shareUrl = encodeURIComponent(window.location.href.split('?')[0]);

      document.getElementById('fpj-share-links').innerHTML = `
        <a href="https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}"
           target="_blank" rel="noopener" class="fpj-share-btn twitter">
          Tweet
        </a>
        <a href="https://www.facebook.com/sharer/sharer.php?u=${shareUrl}&quote=${shareText}"
           target="_blank" rel="noopener" class="fpj-share-btn facebook">
          Share
        </a>
      `;

      // Track completion
      this.trackEvent('fpj_quiz_completed', {
        mode: this.currentMode,
        score: this.score,
        total: this.questions.length,
        percentage: percentage
      });
    }

    getMedalTier(percentage) {
      if (percentage >= 90) {
        return {
          emoji: '🥇',
          title: 'Gold Medal - Journe Connoisseur!',
          message: 'Exceptional! You know F.P. Journe like a Geneva boutique specialist. François-Paul himself would be impressed!'
        };
      } else if (percentage >= 70) {
        return {
          emoji: '🥈',
          title: 'Silver Medal - Serious Collector!',
          message: 'Excellent knowledge of the collection. You clearly appreciate independent watchmaking at its finest.'
        };
      } else if (percentage >= 50) {
        return {
          emoji: '🥉',
          title: 'Bronze Medal - Watch Enthusiast!',
          message: 'Good foundation! Keep studying - these 18K rose gold movements deserve your attention.'
        };
      } else {
        return {
          emoji: '⏱️',
          title: 'Keep Learning!',
          message: 'F.P. Journe is a niche brand with only ~900 pieces per year. Study up and try again!'
        };
      }
    }

    showModeSelect() {
      this.form.style.display = 'none';
      this.results.style.display = 'none';
      this.modeSelect.style.display = 'block';
    }

    showError(message) {
      const container = document.querySelector('.fpj-quiz-container');
      if (container) {
        container.innerHTML = `<p style="text-align: center; color: #dc3545; padding: 40px;">${this.escapeHtml(message)}</p>`;
      }
    }

    shuffle(array) {
      for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
      }
      return array;
    }

    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }

    trackEvent(eventName, params = {}) {
      if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, params);
      }
    }
  }

  window.FPJQuiz = FPJQuiz;
})();
